// S33 方案目錄(/plans)— Plan catalog 版本治理(IA specs/01 §2.3)。
// 版本清單/diff、draft 建立、publish/retire(reason/case 必填、二段確認)。
// published 版本不可原地修改(common/12 §2.1)——本 API 本來就無 update 端點,
// 修訂一律另建新版本;publish/retire 走 durable maker/checker + password step-up。

import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../auth/AuthContext';
import { ApiError } from '../../lib/api';
import { ErrorState } from '../../components/StateBlock';
import { ConfirmDialog } from '../_shared/ConfirmDialog';
import { formatTimestampUs } from '../../lib/format';
import { roleLabel } from '../users/userModel';
import '../_shared/shared.css';
import './commerce.css';
import type { PlanDefinition, PlanPrice } from './commerceApi';
import { createPlanDraft, listPlans, requestCommerceOperation } from './commerceApi';
import { CommerceApprovalPanel } from './CommerceApprovalPanel';
import {
  DUAL_CONTROL_NOTE,
  ENTITLEMENT_FIELDS,
  PLAN_ID_PATTERN,
  audienceLabel,
  billingIntervalLabel,
  canCommerceWrite,
  diffPlanVersions,
  emptyEntitlements,
  missingOpsEntitlementKeys,
  formatEntitlementValue,
  formatMinor,
  parseList,
  planStatusLabel,
  planStatusTone,
  usFromLocalInput,
} from './commerceModel';

function errorText(e: unknown): string {
  if (e instanceof ApiError) return `${e.message}${e.requestId ? `（request_id: ${e.requestId}）` : ''}`;
  return e instanceof Error ? e.message : '操作失敗';
}

function planKey(p: Pick<PlanDefinition, 'plan_id' | 'version'>): string {
  return `${p.plan_id}@${p.version}`;
}

interface PriceRow {
  price_id: string;
  region: string;
  currency: string;
  amount: string;
}

interface DraftForm {
  plan_id: string;
  version: string;
  name: string;
  audience: string;
  family_id: string;
  billing_interval: string;
  purchasable: boolean;
  effective_from: string;
  effective_to: string;
  recording_enabled: boolean;
  recording_modes: string;
  retention_days: string;
  quality_max: string;
  locations: string;
  storage_gb: string;
  event_retention_days: string;
  relay_gb: string;
  device_count: string;
  camera_count: string;
  channel_count: string;
  share_users: string;
  ai_features: string;
  ai_monthly_units: string;
  firmware_channels: string;
  // Ops 軸三欄。刻意留在草稿狀態裡而不是「建立時再補」——後端的
  // PlanEntitlements 這三欄沒有 omitempty,payload 少送即解碼成 false/0/0,
  // 由既有 Ops 方案衍生新版本會因此把 Ops 靜默關掉,publish 後整租戶
  // /v3/ops fail-closed(admin 00 §3.7 S33)。表單以唯讀方式攜帶原值。
  ops_enabled: boolean;
  max_seats: string;
  max_devices: string;
}

const EMPTY_DRAFT: DraftForm = {
  plan_id: '',
  version: '1',
  name: '',
  audience: 'consumer',
  family_id: '',
  billing_interval: 'month',
  purchasable: true,
  effective_from: '',
  effective_to: '',
  recording_enabled: false,
  recording_modes: '',
  retention_days: '0',
  quality_max: '',
  locations: '',
  storage_gb: '0',
  event_retention_days: '0',
  relay_gb: '0',
  device_count: '0',
  camera_count: '0',
  channel_count: '0',
  share_users: '0',
  ops_enabled: false,
  max_seats: '0',
  max_devices: '0',
  ai_features: '',
  ai_monthly_units: '0',
  firmware_channels: '',
};

function intOrZero(v: string): number {
  const n = Number.parseInt(v, 10);
  return Number.isFinite(n) && n >= 0 ? n : 0;
}

const STATUS_FILTERS = [
  { value: '', label: '全部' },
  { value: 'draft', label: '草稿' },
  { value: 'published', label: '已發布' },
  { value: 'retired', label: '已停售' },
] as const;

export default function PlansPage() {
  const { role } = useAuth();
  const writable = canCommerceWrite(role);
  const [plans, setPlans] = useState<PlanDefinition[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState<unknown>(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedKey, setSelectedKey] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');
  const [err, setErr] = useState('');
  const [latestOperationId, setLatestOperationId] = useState('');

  // 生命週期(publish/retire)操作脈絡
  const [lifecycleReason, setLifecycleReason] = useState('');
  const [lifecycleCaseId, setLifecycleCaseId] = useState('');
  const [confirmPublish, setConfirmPublish] = useState(false);
  const [confirmRetire, setConfirmRetire] = useState(false);

  // 建立草稿
  const [draft, setDraft] = useState<DraftForm>(EMPTY_DRAFT);
  const [priceRows, setPriceRows] = useState<PriceRow[]>([]);
  const [draftReason, setDraftReason] = useState('');
  const [draftCaseId, setDraftCaseId] = useState('');

  // 版本比對
  const [diffA, setDiffA] = useState('');
  const [diffB, setDiffB] = useState('');

  async function load() {
    setLoading(true);
    setLoadErr(null);
    try {
      const rows = await listPlans();
      setPlans(rows);
      if (rows.length > 0 && !rows.some((p) => planKey(p) === selectedKey)) setSelectedKey(planKey(rows[0]));
    } catch (e) {
      setLoadErr(e);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const visiblePlans = useMemo(
    () => (statusFilter ? plans.filter((p) => p.status === statusFilter) : plans),
    [plans, statusFilter]
  );
  const selected = useMemo(() => plans.find((p) => planKey(p) === selectedKey) ?? null, [plans, selectedKey]);
  const planIds = useMemo(() => new Set(plans.map((p) => p.plan_id)).size, [plans]);
  const publishedCount = useMemo(() => plans.filter((p) => p.status === 'published').length, [plans]);
  const draftCount = useMemo(() => plans.filter((p) => p.status === 'draft').length, [plans]);

  const planA = useMemo(() => plans.find((p) => planKey(p) === diffA) ?? null, [plans, diffA]);
  const planB = useMemo(() => plans.find((p) => planKey(p) === diffB) ?? null, [plans, diffB]);
  const diffRows = useMemo(() => (planA && planB ? diffPlanVersions(planA, planB) : []), [planA, planB]);

  const lifecycleReady = Boolean(writable && selected && lifecycleReason.trim() && lifecycleCaseId.trim());

  const draftPricesValid =
    priceRows.every(
      (r) =>
        r.price_id.trim() &&
        r.region.trim() &&
        /^[A-Za-z]{3}$/.test(r.currency.trim()) &&
        Number.isFinite(Number.parseInt(r.amount, 10)) &&
        Number.parseInt(r.amount, 10) >= 0
    ) && new Set(priceRows.map((r) => r.price_id.trim())).size === priceRows.length;
  const draftRecordingValid =
    !draft.recording_enabled || (intOrZero(draft.retention_days) > 0 && parseList(draft.recording_modes).length > 0 && draft.quality_max.trim() !== '');
  const draftReady = Boolean(
    writable &&
      PLAN_ID_PATTERN.test(draft.plan_id.trim()) &&
      intOrZero(draft.version) >= 1 &&
      draft.name.trim() &&
      draftRecordingValid &&
      draftPricesValid &&
      draftReason.trim() &&
      draftCaseId.trim()
  );

  function selectPlan(p: PlanDefinition) {
    setSelectedKey(planKey(p));
    setNotice('');
    setErr('');
  }

  // 以所選版本為基底帶入草稿表單(version 自動 +1 同 plan 最大版)
  function prefillFromSelected() {
    if (!selected) return;
    const maxVersion = Math.max(...plans.filter((p) => p.plan_id === selected.plan_id).map((p) => p.version));
    const e = selected.entitlements ?? emptyEntitlements();
    setDraft({
      plan_id: selected.plan_id,
      version: String(maxVersion + 1),
      name: selected.name,
      audience: selected.audience,
      family_id: selected.family_id ?? '',
      billing_interval: selected.billing_interval,
      purchasable: selected.purchasable,
      effective_from: '',
      effective_to: '',
      recording_enabled: e.recording_enabled,
      recording_modes: (e.recording_modes ?? []).join(', '),
      retention_days: String(e.retention_days ?? 0),
      quality_max: e.quality_max ?? '',
      locations: (e.locations ?? []).join(', '),
      storage_gb: String(e.storage_gb ?? 0),
      event_retention_days: String(e.event_retention_days ?? 0),
      relay_gb: String(e.relay_gb ?? 0),
      device_count: String(e.device_count ?? 0),
      camera_count: String(e.camera_count ?? 0),
      channel_count: String(e.channel_count ?? 0),
      share_users: String(e.share_users ?? 0),
      ai_features: (e.ai_features ?? []).join(', '),
      ai_monthly_units: String(e.ai_monthly_units ?? 0),
      firmware_channels: (e.firmware_channels ?? []).join(', '),
      // 由所選版本原樣帶入,不得漏 —— 漏掉就是 S33 的靜默歸零。
      ops_enabled: e.ops_enabled ?? false,
      max_seats: String(e.max_seats ?? 0),
      max_devices: String(e.max_devices ?? 0),
    });
    setPriceRows(
      (selected.prices ?? []).map((p) => ({ price_id: p.price_id, region: p.region, currency: p.currency, amount: String(p.amount_minor) }))
    );
  }

  function buildDraftPayload(): PlanDefinition {
    const prices: PlanPrice[] = priceRows.map((r) => ({
      price_id: r.price_id.trim(),
      region: r.region.trim(),
      currency: r.currency.trim().toUpperCase(),
      amount_minor: intOrZero(r.amount),
      billing_interval: draft.billing_interval,
    }));
    return {
      plan_id: draft.plan_id.trim(),
      version: intOrZero(draft.version),
      status: 'draft',
      name: draft.name.trim(),
      audience: draft.audience,
      family_id: draft.family_id.trim() || undefined,
      billing_interval: draft.billing_interval,
      purchasable: draft.purchasable,
      effective_from: usFromLocalInput(draft.effective_from),
      effective_to: usFromLocalInput(draft.effective_to),
      entitlements: {
        recording_enabled: draft.recording_enabled,
        recording_modes: parseList(draft.recording_modes),
        retention_days: intOrZero(draft.retention_days),
        quality_max: draft.quality_max.trim(),
        locations: parseList(draft.locations),
        storage_gb: intOrZero(draft.storage_gb),
        event_retention_days: intOrZero(draft.event_retention_days),
        relay_gb: intOrZero(draft.relay_gb),
        device_count: intOrZero(draft.device_count),
        camera_count: intOrZero(draft.camera_count),
        channel_count: intOrZero(draft.channel_count),
        share_users: intOrZero(draft.share_users),
        ai_features: parseList(draft.ai_features),
        ai_monthly_units: intOrZero(draft.ai_monthly_units),
        firmware_channels: parseList(draft.firmware_channels),
        // 一律顯式送出。後端(restful!391 起)對缺任一欄回 400,因為
        // 「沒送」與「送 false/0」在 JSON 解碼後無法區分,而那個歧義曾讓
        // 衍生版本 publish 後關掉整租戶的 Ops。
        ops_enabled: draft.ops_enabled,
        max_seats: intOrZero(draft.max_seats),
        max_devices: intOrZero(draft.max_devices),
      },
      prices,
    };
  }

  async function submitDraft() {
    setBusy(true);
    setErr('');
    setNotice('');
    try {
      const payload = buildDraftPayload();
      // 伺服端 400 的前端鏡像。存在的理由是**位置**:草稿在瀏覽器組出來,而缺欄的
      // 後果(publish 後整租戶 /v3/ops fail-closed)要到很久以後才看得見;在送出前
      // 擋住,錯誤訊息才指得到真正的原因。
      const missing = missingOpsEntitlementKeys(payload.entitlements);
      if (missing.length > 0) {
        setErr(`草稿未帶 Ops 軸欄位:${missing.join('、')}。缺欄會被後端解碼成關閉/0,由既有 Ops 方案衍生新版本時會靜默關掉 Ops。`);
        return;
      }
      const created = await createPlanDraft(payload, { reason: draftReason.trim(), case_id: draftCaseId.trim() });
      setPlans((rows) => [...rows.filter((p) => planKey(p) !== planKey(created)), created].sort((a, b) => (a.plan_id === b.plan_id ? a.version - b.version : a.plan_id < b.plan_id ? -1 : 1)));
      setSelectedKey(planKey(created));
      setNotice(`已建立草稿 ${planKey(created)}(發布前不對客戶生效)`);
      setDraft(EMPTY_DRAFT);
      setPriceRows([]);
      setDraftReason('');
      setDraftCaseId('');
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  async function submitPublish() {
    if (!selected) return;
    setBusy(true);
    try {
      const op = await requestCommerceOperation(
        'plan.publish',
        { plan_id: selected.plan_id, version: selected.version },
        { reason: lifecycleReason.trim(), case_id: lifecycleCaseId.trim() }
      );
      setLatestOperationId(op.operation_id);
      setNotice(`已建立發布覆核單 ${op.operation_id}；尚未對客戶生效，等待不同管理員核准。`);
      setConfirmPublish(false);
      setErr('');
      setLifecycleReason('');
      setLifecycleCaseId('');
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  async function submitRetire() {
    if (!selected) return;
    setBusy(true);
    try {
      const op = await requestCommerceOperation(
        'plan.retire',
        { plan_id: selected.plan_id, version: selected.version },
        { reason: lifecycleReason.trim(), case_id: lifecycleCaseId.trim() }
      );
      setLatestOperationId(op.operation_id);
      setNotice(`已建立停售覆核單 ${op.operation_id}；核准前方案狀態不變。`);
      setConfirmRetire(false);
      setErr('');
      setLifecycleReason('');
      setLifecycleCaseId('');
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="commerce-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">方案目錄</h1>
          <p className="page-subtitle">Plan 版本治理:草稿 → 發布(不可原地修改)→ 停售;含 權益項 / 區域價格 / 版本比對。</p>
        </div>
        <div className="adm-actionbar-btns">
          <span className={`pill ${writable ? 'pill-good' : 'pill-neutral'}`}>{role ? roleLabel(role) : '其他角色'}</span>
          <button className="btn-ghost btn-sm" onClick={() => void load()} disabled={loading || busy}>
            {loading ? '同步中' : '重新整理'}
          </button>
        </div>
      </div>

      {notice && (
        <p className="adm-action-result is-ok" role="status">
          {notice}
        </p>
      )}
      {err && !confirmPublish && !confirmRetire && (
        <p className="adm-action-result is-err" role="alert">
          {err}
        </p>
      )}

      <div className="kpi-strip" aria-label="方案統計">
        <div className="kpi-card kpi-card--hero">
          <span className="kpi-label">方案</span>
          <span className="kpi-value">{planIds}</span>
          <span className="kpi-foot">{plans.length} 個版本</span>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">已發布</span>
          <span className="kpi-value">{publishedCount}</span>
          <span className="kpi-foot">客戶可見版本</span>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">草稿</span>
          <span className="kpi-value">{draftCount}</span>
          <span className="kpi-foot">待發布</span>
        </div>
        <div className="kpi-card kpi-card--secondary">
          <span className="kpi-label">已停售</span>
          <span className="kpi-value">{plans.filter((p) => p.status === 'retired').length}</span>
          <span className="kpi-foot">既有訂閱仍可解析</span>
        </div>
      </div>

      <div className="commerce-split">
        <section className="card">
          <div className="section-head">
            <div>
              <h2 className="section-title">版本清單</h2>
              <span className="section-meta adm-mono">{selected ? planKey(selected) : '尚未選取'}</span>
            </div>
            <div className="segmented-control" aria-label="狀態篩選">
              {STATUS_FILTERS.map((f) => (
                <button key={f.value || 'all'} className={statusFilter === f.value ? 'is-active' : ''} onClick={() => setStatusFilter(f.value)}>
                  {f.label}
                </button>
              ))}
            </div>
          </div>
          {loadErr ? (
            <ErrorState error={loadErr} onRetry={() => void load()} />
          ) : visiblePlans.length > 0 ? (
            <div className="table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>方案 / 版本</th>
                    <th>狀態</th>
                    <th>客群</th>
                    <th>計費</th>
                    <th>可購買</th>
                    <th>價格數</th>
                    <th>發布時間</th>
                  </tr>
                </thead>
                <tbody>
                  {visiblePlans.map((p) => (
                    <tr key={planKey(p)} className={`clickable ${selectedKey === planKey(p) ? 'is-selected' : ''}`} onClick={() => selectPlan(p)}>
                      <td>
                        <div className="adm-cell-strong">{p.name || p.plan_id}</div>
                        <div className="adm-mono text-tertiary">{planKey(p)}</div>
                      </td>
                      <td>
                        <span className={`pill ${planStatusTone(p.status)}`}>{planStatusLabel(p.status)}</span>
                      </td>
                      <td>{audienceLabel(p.audience)}</td>
                      <td>{billingIntervalLabel(p.billing_interval)}</td>
                      <td>{p.purchasable ? '是' : '否'}</td>
                      <td>{(p.prices ?? []).length}</td>
                      <td className="adm-mono">{formatTimestampUs(p.published_at, { second: undefined })}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="empty-state">
              <div className="empty-state-icon">📦</div>
              <div className="empty-state-title">{loading ? '載入中…' : '目前沒有符合條件的方案版本'}</div>
              <div className="empty-state-detail">{loading ? '' : '調整狀態篩選,或在右側建立第一個草稿版本。'}</div>
            </div>
          )}
        </section>

        <aside className="commerce-side">
          <section className="card">
            <div className="section-head">
              <h2 className="section-title">版本生命週期</h2>
            </div>
            {selected ? (
              <div className="commerce-form">
                <div>
                  <div className="adm-cell-strong">{selected.name || selected.plan_id}</div>
                  <div className="adm-mono text-tertiary">{planKey(selected)}</div>
                  <span className={`pill ${planStatusTone(selected.status)}`}>目前狀態:{planStatusLabel(selected.status)}</span>
                </div>
                {selected.status === 'published' && (
                  <p className="commerce-hint text-tertiary">已發布版本唯讀鎖定,不可原地修改;要調整內容請以此版本為基底另建新草稿。</p>
                )}
                {writable ? (
                  <>
                    <div className="commerce-form-grid">
                      <label className="adm-field">
                        <span className="adm-field-label text-secondary">原因</span>
                        <input value={lifecycleReason} onChange={(e) => setLifecycleReason(e.target.value)} disabled={busy} />
                      </label>
                      <label className="adm-field">
                        <span className="adm-field-label text-secondary">案件編號</span>
                        <input value={lifecycleCaseId} onChange={(e) => setLifecycleCaseId(e.target.value)} disabled={busy} />
                      </label>
                    </div>
                    {selected.status === 'draft' && (
                      <>
                        <p className="commerce-dc-note">{DUAL_CONTROL_NOTE}</p>
                        <button className="btn-primary" disabled={busy || !lifecycleReady} onClick={() => { setErr(''); setConfirmPublish(true); }}>
                          發布此版本
                        </button>
                      </>
                    )}
                    {selected.status === 'published' && (
                      <button className="btn-danger" disabled={busy || !lifecycleReady} onClick={() => { setErr(''); setConfirmRetire(true); }}>
                        停售此版本
                      </button>
                    )}
                    {selected.status === 'retired' && <p className="commerce-hint text-tertiary">已停售版本無可用生命週期動作。</p>}
                    {!lifecycleReady && selected.status !== 'retired' && (
                      <span className="commerce-hint text-tertiary">需填寫原因與案件編號(將寫入稽核)。</span>
                    )}
                  </>
                ) : (
                  <p className="commerce-hint text-tertiary">目前角色僅能檢視方案目錄;發布/停售由 admin / platform-owner 執行。</p>
                )}
              </div>
            ) : (
              <p className="commerce-hint text-tertiary">請先於左側選取一個版本。</p>
            )}
          </section>

          <section className="card card--primary">
            <div className="section-head">
              <h2 className="section-title">建立草稿版本</h2>
              {selected && writable && (
                <button className="btn-ghost btn-sm" onClick={prefillFromSelected} disabled={busy}>
                  從所選版本帶入
                </button>
              )}
            </div>
            {writable ? (
              <div className="commerce-form">
                <div className="commerce-form-grid">
                  <label className="adm-field">
                    <span className="adm-field-label text-secondary">方案 ID</span>
                    <input value={draft.plan_id} onChange={(e) => setDraft({ ...draft, plan_id: e.target.value })} placeholder="home-plus" disabled={busy} />
                  </label>
                  <label className="adm-field">
                    <span className="adm-field-label text-secondary">版本</span>
                    <input value={draft.version} onChange={(e) => setDraft({ ...draft, version: e.target.value })} inputMode="numeric" disabled={busy} />
                  </label>
                </div>
                <label className="adm-field">
                  <span className="adm-field-label text-secondary">名稱</span>
                  <input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="Home Plus" disabled={busy} />
                </label>
                <div className="commerce-form-grid commerce-form-grid--3">
                  <label className="adm-field">
                    <span className="adm-field-label text-secondary">客群</span>
                    <select value={draft.audience} onChange={(e) => setDraft({ ...draft, audience: e.target.value })} disabled={busy}>
                      <option value="consumer">消費者</option>
                      <option value="business">企業</option>
                      <option value="partner">夥伴</option>
                      <option value="internal">內部</option>
                    </select>
                  </label>
                  <label className="adm-field">
                    <span className="adm-field-label text-secondary">計費週期</span>
                    <select value={draft.billing_interval} onChange={(e) => setDraft({ ...draft, billing_interval: e.target.value })} disabled={busy}>
                      <option value="day">日繳</option>
                      <option value="week">週繳</option>
                      <option value="month">月繳</option>
                      <option value="year">年繳</option>
                      <option value="one_time">一次性</option>
                    </select>
                  </label>
                  <label className="adm-field">
                    <span className="adm-field-label text-secondary">品牌家族(選填)</span>
                    <input value={draft.family_id} onChange={(e) => setDraft({ ...draft, family_id: e.target.value })} placeholder="fam-platform" disabled={busy} />
                  </label>
                </div>
                <label className="commerce-checkbox">
                  <input type="checkbox" checked={draft.purchasable} onChange={(e) => setDraft({ ...draft, purchasable: e.target.checked })} disabled={busy} />
                  <span>可購買(發布時可購買版本必須至少一筆價格)</span>
                </label>

                <div className="section-head">
                  <h3 className="section-title">權益項</h3>
                </div>
                <label className="commerce-checkbox">
                  <input type="checkbox" checked={draft.recording_enabled} onChange={(e) => setDraft({ ...draft, recording_enabled: e.target.checked })} disabled={busy} />
                  <span>啟用雲端錄影(啟用時需填保留天數 / 模式 / 最高畫質)</span>
                </label>
                <div className="commerce-form-grid commerce-form-grid--3">
                  <label className="adm-field">
                    <span className="adm-field-label text-secondary">錄影保留(天)</span>
                    <input value={draft.retention_days} onChange={(e) => setDraft({ ...draft, retention_days: e.target.value })} inputMode="numeric" disabled={busy} />
                  </label>
                  <label className="adm-field">
                    <span className="adm-field-label text-secondary">最高畫質</span>
                    <input value={draft.quality_max} onChange={(e) => setDraft({ ...draft, quality_max: e.target.value })} placeholder="2k" disabled={busy} />
                  </label>
                  <label className="adm-field">
                    <span className="adm-field-label text-secondary">錄影模式(逗號分隔)</span>
                    <input value={draft.recording_modes} onChange={(e) => setDraft({ ...draft, recording_modes: e.target.value })} placeholder="continuous, event" disabled={busy} />
                  </label>
                  <label className="adm-field">
                    <span className="adm-field-label text-secondary">儲存位置(逗號分隔)</span>
                    <input value={draft.locations} onChange={(e) => setDraft({ ...draft, locations: e.target.value })} placeholder="cloud, sdcard" disabled={busy} />
                  </label>
                  <label className="adm-field">
                    <span className="adm-field-label text-secondary">儲存空間(GB)</span>
                    <input value={draft.storage_gb} onChange={(e) => setDraft({ ...draft, storage_gb: e.target.value })} inputMode="numeric" disabled={busy} />
                  </label>
                  <label className="adm-field">
                    <span className="adm-field-label text-secondary">事件保留(天)</span>
                    <input value={draft.event_retention_days} onChange={(e) => setDraft({ ...draft, event_retention_days: e.target.value })} inputMode="numeric" disabled={busy} />
                  </label>
                  <label className="adm-field">
                    <span className="adm-field-label text-secondary">中繼流量(GB)</span>
                    <input value={draft.relay_gb} onChange={(e) => setDraft({ ...draft, relay_gb: e.target.value })} inputMode="numeric" disabled={busy} />
                  </label>
                  <label className="adm-field">
                    <span className="adm-field-label text-secondary">裝置數上限</span>
                    <input value={draft.device_count} onChange={(e) => setDraft({ ...draft, device_count: e.target.value })} inputMode="numeric" disabled={busy} />
                  </label>
                  <label className="adm-field">
                    <span className="adm-field-label text-secondary">攝影機數上限</span>
                    <input value={draft.camera_count} onChange={(e) => setDraft({ ...draft, camera_count: e.target.value })} inputMode="numeric" disabled={busy} />
                  </label>
                  <label className="adm-field">
                    <span className="adm-field-label text-secondary">通道數上限</span>
                    <input value={draft.channel_count} onChange={(e) => setDraft({ ...draft, channel_count: e.target.value })} inputMode="numeric" disabled={busy} />
                  </label>
                  <label className="adm-field">
                    <span className="adm-field-label text-secondary">分享人數上限</span>
                    <input value={draft.share_users} onChange={(e) => setDraft({ ...draft, share_users: e.target.value })} inputMode="numeric" disabled={busy} />
                  </label>
                  <label className="adm-field">
                    <span className="adm-field-label text-secondary">AI 月額度</span>
                    <input value={draft.ai_monthly_units} onChange={(e) => setDraft({ ...draft, ai_monthly_units: e.target.value })} inputMode="numeric" disabled={busy} />
                  </label>
                </div>
                <div className="commerce-form-grid">
                  <label className="adm-field">
                    <span className="adm-field-label text-secondary">AI 功能(逗號分隔)</span>
                    <input value={draft.ai_features} onChange={(e) => setDraft({ ...draft, ai_features: e.target.value })} placeholder="person, package" disabled={busy} />
                  </label>
                  <label className="adm-field">
                    <span className="adm-field-label text-secondary">韌體通道(逗號分隔)</span>
                    <input value={draft.firmware_channels} onChange={(e) => setDraft({ ...draft, firmware_channels: e.target.value })} placeholder="stable, beta" disabled={busy} />
                  </label>
                </div>

                <div className="section-head">
                  <h3 className="section-title">區域價格</h3>
                  <button className="btn-ghost btn-sm" disabled={busy} onClick={() => setPriceRows((rows) => [...rows, { price_id: '', region: '', currency: 'USD', amount: '0' }])}>
                    新增價格
                  </button>
                </div>
                <div className="commerce-rows-editor">
                  {priceRows.length === 0 && <span className="commerce-hint text-tertiary">尚無價格;非賣品(不可購買)可留空。</span>}
                  {priceRows.map((row, i) => (
                    <div className="commerce-row-line commerce-row-line--price" key={i}>
                      <label className="adm-field">
                        <span className="adm-field-label text-secondary">價格 ID</span>
                        <input value={row.price_id} onChange={(e) => setPriceRows((rows) => rows.map((r, j) => (j === i ? { ...r, price_id: e.target.value } : r)))} placeholder="price-us-monthly" disabled={busy} />
                      </label>
                      <label className="adm-field">
                        <span className="adm-field-label text-secondary">區域</span>
                        <input value={row.region} onChange={(e) => setPriceRows((rows) => rows.map((r, j) => (j === i ? { ...r, region: e.target.value } : r)))} placeholder="us" disabled={busy} />
                      </label>
                      <label className="adm-field">
                        <span className="adm-field-label text-secondary">幣別</span>
                        <input value={row.currency} onChange={(e) => setPriceRows((rows) => rows.map((r, j) => (j === i ? { ...r, currency: e.target.value } : r)))} placeholder="USD" disabled={busy} />
                      </label>
                      <label className="adm-field">
                        <span className="adm-field-label text-secondary">金額(minor units)</span>
                        <input value={row.amount} onChange={(e) => setPriceRows((rows) => rows.map((r, j) => (j === i ? { ...r, amount: e.target.value } : r)))} inputMode="numeric" disabled={busy} />
                      </label>
                      <button className="btn-ghost btn-sm" disabled={busy} onClick={() => setPriceRows((rows) => rows.filter((_, j) => j !== i))}>
                        移除
                      </button>
                    </div>
                  ))}
                </div>

                <div className="commerce-form-grid">
                  <label className="adm-field">
                    <span className="adm-field-label text-secondary">原因</span>
                    <input value={draftReason} onChange={(e) => setDraftReason(e.target.value)} disabled={busy} />
                  </label>
                  <label className="adm-field">
                    <span className="adm-field-label text-secondary">案件編號</span>
                    <input value={draftCaseId} onChange={(e) => setDraftCaseId(e.target.value)} disabled={busy} />
                  </label>
                </div>
                <button className="btn-primary" disabled={busy || !draftReady} onClick={() => void submitDraft()}>
                  建立草稿
                </button>
                <span className="commerce-hint text-tertiary">
                  Plan 版本不可變:草稿建立後亦無原地編輯端點,修訂=另建新版本;plan_id 限小寫英數與連字號。所有建立將寫入稽核。
                </span>
              </div>
            ) : (
              <p className="commerce-hint text-tertiary">目前角色僅能檢視方案目錄。</p>
            )}
          </section>
        </aside>
      </div>

      {selected && (
        <section className="workbench-panel">
          <div className={`workbench-panel-head accent-rail accent-rail--${selected.status === 'published' ? 'good' : selected.status === 'draft' ? 'info' : 'neutral'}`}>
            <div>
              <h2>
                {selected.name || selected.plan_id} <span className="adm-mono text-tertiary">{planKey(selected)}</span>
              </h2>
              <span className="text-tertiary">
                {audienceLabel(selected.audience)}・{billingIntervalLabel(selected.billing_interval)}
                {selected.family_id ? `・家族 ${selected.family_id}` : ''}
              </span>
            </div>
            <div className="adm-actionbar-btns">
              <span className={`pill ${planStatusTone(selected.status)}`}>{planStatusLabel(selected.status)}</span>
              <span className="pill pill-neutral">{selected.purchasable ? '可購買' : '非賣品'}</span>
            </div>
          </div>
          <div className="commerce-form" style={{ padding: 'var(--spacing-8)' }}>
            <div className="section-head">
              <h3 className="section-title">權益項</h3>
            </div>
            <div className="commerce-ent-grid">
              {ENTITLEMENT_FIELDS.map((def) => (
                <div className="commerce-ent-item" key={String(def.key)}>
                  <span className="commerce-ent-label">{def.label}</span>
                  <span className="commerce-ent-value">{formatEntitlementValue(def, selected.entitlements)}</span>
                </div>
              ))}
            </div>
            <div className="section-head">
              <h3 className="section-title">區域價格</h3>
            </div>
            {(selected.prices ?? []).length > 0 ? (
              <div className="table-wrap">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>價格 ID</th>
                      <th>區域</th>
                      <th>金額</th>
                      <th>計費週期</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(selected.prices ?? []).map((p) => (
                      <tr key={p.price_id}>
                        <td className="adm-mono">{p.price_id}</td>
                        <td>{p.region}</td>
                        <td className="adm-mono">{formatMinor(p.amount_minor, p.currency)}</td>
                        <td>{billingIntervalLabel(p.billing_interval)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="commerce-hint text-tertiary">此版本沒有公開價格書(平台 price 參照;Stripe secret 價格映射僅存在伺服端設定,不進瀏覽器)。</p>
            )}
            <div className="section-head">
              <h3 className="section-title">生命週期軌跡</h3>
            </div>
            <div className="commerce-tx-meta">
              <div className="commerce-ent-item">
                <span className="commerce-ent-label">建立</span>
                <span className="commerce-ent-value">
                  {formatTimestampUs(selected.created_at, { second: undefined })}
                  {selected.created_by ? ` · ${selected.created_by}` : ''}
                </span>
              </div>
              <div className="commerce-ent-item">
                <span className="commerce-ent-label">發布</span>
                <span className="commerce-ent-value">
                  {formatTimestampUs(selected.published_at, { second: undefined })}
                  {selected.published_by ? ` · ${selected.published_by}` : ''}
                </span>
              </div>
              <div className="commerce-ent-item">
                <span className="commerce-ent-label">停售</span>
                <span className="commerce-ent-value">
                  {formatTimestampUs(selected.retired_at, { second: undefined })}
                  {selected.retired_by ? ` · ${selected.retired_by}` : ''}
                </span>
              </div>
              <div className="commerce-ent-item">
                <span className="commerce-ent-label">生效期間</span>
                <span className="commerce-ent-value">
                  {selected.effective_from || selected.effective_to
                    ? `${formatTimestampUs(selected.effective_from, { second: undefined })} → ${selected.effective_to ? formatTimestampUs(selected.effective_to, { second: undefined }) : '無期限'}`
                    : '無限制'}
                </span>
              </div>
            </div>
          </div>
        </section>
      )}

      <section className="card">
        <div className="section-head">
          <div>
            <h2 className="section-title">版本比對</h2>
            <span className="section-meta">entitlements / limits / 區域價格逐欄比較(前端計算)</span>
          </div>
        </div>
        <div className="commerce-query-row" style={{ marginBottom: 'var(--spacing-6)' }}>
          <label className="adm-field">
            <span className="adm-field-label text-secondary">基準版本 A</span>
            <select value={diffA} onChange={(e) => setDiffA(e.target.value)}>
              <option value="">選擇版本</option>
              {plans.map((p) => (
                <option key={planKey(p)} value={planKey(p)}>
                  {planKey(p)}({planStatusLabel(p.status)})
                </option>
              ))}
            </select>
          </label>
          <label className="adm-field">
            <span className="adm-field-label text-secondary">比較版本 B</span>
            <select value={diffB} onChange={(e) => setDiffB(e.target.value)}>
              <option value="">選擇版本</option>
              {plans.map((p) => (
                <option key={planKey(p)} value={planKey(p)}>
                  {planKey(p)}({planStatusLabel(p.status)})
                </option>
              ))}
            </select>
          </label>
        </div>
        {planA && planB ? (
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>欄位</th>
                  <th>
                    A:<span className="adm-mono">{planKey(planA)}</span>
                  </th>
                  <th>
                    B:<span className="adm-mono">{planKey(planB)}</span>
                  </th>
                  <th>異動</th>
                </tr>
              </thead>
              <tbody>
                {diffRows.map((row) => (
                  <tr key={row.key} className={row.changed ? 'commerce-diff-changed' : ''}>
                    <td>{row.label}</td>
                    <td className="commerce-diff-cell">{row.a}</td>
                    <td className="commerce-diff-cell">{row.b}</td>
                    <td>{row.changed ? <span className="pill pill-warn">變更</span> : <span className="text-tertiary">—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="commerce-hint text-tertiary">選擇兩個版本(通常為同一 plan 的相鄰版本)以檢視逐欄差異。</p>
        )}
      </section>

      <CommerceApprovalPanel operationId={latestOperationId} onCompleted={() => void load()} />

      {confirmPublish && selected && (
        <ConfirmDialog
          title="發布方案版本"
          tone="primary"
          confirmLabel="確認發布"
          busy={busy}
          error={err || undefined}
          warning={DUAL_CONTROL_NOTE}
          body={
            <>
              <p>
                將發布 <span className="adm-mono">{planKey(selected)}</span>({selected.name})。發布後此版本<strong>唯讀鎖定、不可原地修改</strong>
                ,且對{audienceLabel(selected.audience)}客群可見{selected.purchasable ? '並可購買' : '(非賣品)'}。
              </p>
              <p className="text-tertiary">處置理由:{lifecycleReason.trim()}・案件:{lifecycleCaseId.trim()}(將寫入稽核)</p>
            </>
          }
          onConfirm={() => void submitPublish()}
          onCancel={() => setConfirmPublish(false)}
        />
      )}

      {confirmRetire && selected && (
        <ConfirmDialog
          title="停售方案版本"
          tone="danger"
          confirmLabel="確認停售"
          busy={busy}
          error={err || undefined}
          warning="停售後停止新購;既有訂閱仍可解析此版本,不影響現有客戶權益。"
          body={
            <>
              <p>
                將停售 <span className="adm-mono">{planKey(selected)}</span>({selected.name})。
              </p>
              <p className="text-tertiary">處置理由:{lifecycleReason.trim()}・案件:{lifecycleCaseId.trim()}(將寫入稽核)</p>
            </>
          }
          onConfirm={() => void submitRetire()}
          onCancel={() => setConfirmRetire(false)}
        />
      )}
    </div>
  );
}
